import CheckOutlineIcon from "@rsuite/icons/CheckOutline";
import ReloadIcon from "@rsuite/icons/Reload";
import type React from "react";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import type { ReactNode } from "react-markdown/lib/react-markdown";
import { ButtonToolbar, IconButton } from "rsuite";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import { extractBoldWords, shuffle } from "../../../shared/helpers";
import style from "./FillInGapsText.module.scss";
import { DropPlaceholder } from "./components/DropPlaceholder";
import { Word } from "./components/Word";

type Props = {
	data: string;
	activityId: string;
};

const FillInGapsText: React.FC<Props> = ({ data, activityId }) => {
	const originalWords = extractBoldWords(data);
	const prefixClass = "fill-in-gaps";
	const [fireworksTrigger, setFireworksTrigger] = useState(false);
	const { triggerActivityCompletion } = useActivityCompletion();
	const [wordsToDrop, setWordsToDrop] = useState([
		{
			word: "",
			originalIndex: 0,
		},
	]);

	const [words, setWords] = useState<
		{ word: string; originalIndex: number; correct: undefined | boolean }[]
	>([
		{
			word: "",
			originalIndex: 0,
			correct: undefined,
		},
	]);
	/* TODO: implement this */
	// const [canPressCheck, setCanPressCheck] = useState(false);
	const [correctItems, setCorrectItems] = useState(0);

	const initialize = () => {
		const shuffledWords = shuffle(originalWords);
		const extendedWords = shuffledWords.map((word, index) => ({
			word,
			originalIndex: index,
		}));

		setWordsToDrop(extendedWords);
		setWords(
			extendedWords.map((item) => ({
				word: "",
				originalIndex: item.originalIndex,
				correct: undefined,
			})),
		);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		initialize();
	}, []);

	const handleDropWord = (
		originalIndex: number,
		wordToDrop: string,
		currentPlaceholderIndex: number,
	) => {
		const newWords = [...words];

		newWords[currentPlaceholderIndex] = {
			word: wordToDrop,
			originalIndex,
			correct: undefined,
		};

		setWords(newWords);

		const newWordsToDrop = [...wordsToDrop];
		newWordsToDrop[originalIndex] = {
			word: "",
			originalIndex,
		};

		setWordsToDrop(newWordsToDrop);
	};

	const handleRemoveWord = (
		{ word, originalIndex }: { word: string; originalIndex: number },
		currentPlaceholderIndex: number,
	) => {
		const newWordsToDrop = [...wordsToDrop];
		newWordsToDrop[originalIndex] = { word, originalIndex };
		setWordsToDrop(newWordsToDrop);

		const newWords = [...words];

		newWords[currentPlaceholderIndex] = {
			word: "",
			originalIndex: currentPlaceholderIndex,
			correct: undefined,
		};

		setWords(newWords);
	};

	const strongRenderer = ({ children }: { children: string[] | ReactNode }) => {
		const currentPlaceholderIndex = Array.isArray(children)
			? originalWords.indexOf(children[0] || ("" as string))
			: 0;
		const wordToDisplay = words[currentPlaceholderIndex] || "";
		return (
			<DropPlaceholder
				onRemoveWord={handleRemoveWord}
				onDropWord={handleDropWord}
				droppedWord={wordToDisplay}
				correct={wordToDisplay.correct}
				currentPlaceholderIndex={currentPlaceholderIndex}
			/>
		);
	};

	const resetActivity = () => {
		initialize();
	};

	const checkAnswersHandler = async () => {
		const doneWords = words.map((word, index) => {
			if (originalWords[index] === word.word) {
				return { ...word, correct: true };
			}
			return { ...word, correct: false };
		});

		setWords(doneWords);

		const isCorrect = doneWords.filter(
			(word, index) => word.word === originalWords[index],
		);
		const allCorrect = isCorrect.length === originalWords.length;
		setCorrectItems(isCorrect.length);
		if (allCorrect) {
			setFireworksTrigger(true);
			await triggerActivityCompletion(activityId, 16, resetActivity);
		}
	};

	const handleFireworksComplete = () => {
		setFireworksTrigger(false);
	};

	return (
		<div className={style[`${prefixClass}__container`]}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={handleFireworksComplete}
				/>
			)}
			<div className={style[`${prefixClass}__toolbar`]}>
				<ButtonToolbar>
					<IconButton
						appearance="primary"
						onClick={resetActivity}
						icon={<ReloadIcon color="white" />}
					/>
					<IconButton
						disabled={false}
						appearance="primary"
						onClick={checkAnswersHandler}
						icon={<CheckOutlineIcon color="white" />}
					>
						Check
					</IconButton>
				</ButtonToolbar>
				<span className={style[`${prefixClass}__score`]}>
					<strong>{`${correctItems} / ${words.length}`}</strong>
				</span>
			</div>
			<div className={style[`${prefixClass}__inner-container`]}>
				<div className={style[`${prefixClass}__top-strip`]}>
					{wordsToDrop.map(({ word, originalIndex }) => (
						<Word word={{ word, originalIndex }} key={originalIndex} />
					))}
				</div>
				<div className={style[`${prefixClass}__main-text`]}>
					<ReactMarkdown components={{ strong: strongRenderer }}>
						{data}
					</ReactMarkdown>
				</div>
			</div>
		</div>
	);
};

export default FillInGapsText;
